import React, { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Search,
  Calendar,
  Crown,
  User,
  Settings,
  ShieldCheck,
  LogOut,
  Sparkles,
  AlertCircle,
  RefreshCw,
  ArrowRight,
  Menu,
  X,
  Heart,
  Receipt,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { PairUpLogo } from '../common/PairUpLogo';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { useLanguage } from '../../i18n/LanguageContext';

interface DashboardLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  requireAuth?: boolean;
  onOpenAuth?: (mode: 'login' | 'signup') => void;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  title,
  subtitle,
  action,
  isLoading = false,
  error = null,
  onRetry,
  requireAuth = true,
  onOpenAuth,
}) => {
  const { currentUser, userProfile, accountType, isAuthenticated, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const isProfileActive =
    userProfile?.companion_profile_status === 'Active' &&
    userProfile?.payment_verified === true &&
    userProfile?.payment_status === 'Success';

  const navItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/companions', label: t('nav.companions'), icon: Search },
    { path: '/bookings', label: t('nav.bookings'), icon: Calendar },
    { path: '/membership', label: t('nav.membership'), icon: Crown },
    { path: '/payment-history', label: t('nav.paymentHistory'), icon: Receipt },
    { path: '/profile', label: t('nav.profile'), icon: User },
    { path: '/settings', label: t('nav.settings'), icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Universal Navbar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-2xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Logo & Platform Tag */}
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2.5 group">
              <PairUpLogo className="w-9 h-9 shrink-0 drop-shadow-xs group-hover:scale-105 transition-transform" />
              <div>
                <span className="text-lg font-black tracking-tight text-slate-900 flex items-center gap-1">
                  PairUp<span className="text-pink-600">Today</span>
                </span>
                <span className="text-[10px] text-slate-400 block -mt-1 font-semibold">
                  Verified Social Platform
                </span>
              </div>
            </Link>

            {/* Desktop Navigation Links */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                      isActive
                        ? 'bg-pink-50 text-pink-700 font-extrabold shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-pink-600' : 'text-slate-400'}`} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* User Account Controls */}
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="compact" />
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                {/* Profile Status Badge */}
                {accountType === 'companion' && (
                  <span
                    className={`hidden sm:inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                      isProfileActive
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-rose-50 text-rose-700 border border-rose-200'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${isProfileActive ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                    {isProfileActive ? 'Active' : 'Inactive'}
                  </span>
                )}

                <Link
                  to="/profile"
                  className="flex items-center gap-2.5 p-1.5 pr-3 rounded-full hover:bg-slate-100 transition-colors"
                >
                  <img
                    src={
                      userProfile?.avatarUrl ||
                      'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=150&q=80'
                    }
                    alt={currentUser || 'User'}
                    className="w-8 h-8 rounded-full object-cover border border-pink-200"
                  />
                  <div className="hidden sm:block text-left text-xs">
                    <strong className="block font-bold text-slate-800 leading-tight">
                      {userProfile?.displayName || currentUser}
                    </strong>
                    <span className="text-[10px] text-slate-400 capitalize">{accountType} Account</span>
                  </div>
                </Link>

                <button
                  type="button"
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                  title="Sign Out"
                  className="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onOpenAuth ? onOpenAuth('login') : navigate('/?auth=login')}
                  className="px-4 py-2 text-xs font-bold text-slate-700 hover:text-slate-900 cursor-pointer"
                >
                  Log In
                </button>
                <button
                  type="button"
                  onClick={() => onOpenAuth ? onOpenAuth('signup') : navigate('/?auth=signup')}
                  className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer"
                >
                  Sign Up
                </button>
              </div>
            )}

            {/* Mobile Hamburger Button */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-xl text-slate-600 hover:bg-slate-100 cursor-pointer"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white px-4 pt-2 pb-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full px-3.5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 ${
                    isActive ? 'bg-pink-50 text-pink-700 font-extrabold' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-pink-600' : 'text-slate-400'}`} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
              <Link to="/dashboard" className="hover:text-pink-600 transition-colors">
                Dashboard
              </Link>
              <span>/</span>
              <span className="text-slate-600 font-semibold">{title}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">{title}</h1>
            {subtitle && <p className="text-xs sm:text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>

          {action && <div className="shrink-0">{action}</div>}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="py-20 text-center space-y-4">
            <div className="w-12 h-12 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin mx-auto" />
            <p className="text-xs font-bold text-slate-500">Loading {title} details...</p>
          </div>
        )}

        {/* API Error State with Retry Button */}
        {!isLoading && error && (
          <div className="p-6 bg-rose-50 border border-rose-200 rounded-3xl text-center space-y-3 max-w-lg mx-auto">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
              <AlertCircle className="w-6 h-6" />
            </div>
            <h3 className="font-extrabold text-rose-950 text-sm">Failed to Load {title}</h3>
            <p className="text-xs text-rose-800">{error}</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow-xs inline-flex items-center gap-1.5 cursor-pointer"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Retry</span>
              </button>
            )}
          </div>
        )}

        {/* Authentication Wall for Protected Pages */}
        {!isLoading && !error && requireAuth && !isAuthenticated && (
          <div className="p-8 sm:p-12 bg-white border border-slate-200 rounded-3xl text-center max-w-md mx-auto shadow-sm space-y-4">
            <div className="w-14 h-14 rounded-2xl bg-pink-100 text-pink-600 flex items-center justify-center mx-auto">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-black text-slate-900">Sign In Required</h2>
              <p className="text-xs text-slate-500">
                Please log in or create a free account to access your {title.toLowerCase()}.
              </p>
            </div>
            <button
              type="button"
              onClick={() => (onOpenAuth ? onOpenAuth('login') : navigate('/?auth=login'))}
              className="w-full py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-bold text-xs rounded-xl shadow-md cursor-pointer flex items-center justify-center gap-2"
            >
              <span>Continue to Login</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Render Page Content */}
        {!isLoading && !error && (!requireAuth || isAuthenticated) && children}
      </main>

      {/* Footer */}
      <footer className="mt-auto border-t border-slate-200 bg-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <span>&copy; 2026 PairUpToday Inc. Safe & Verified Companions.</span>
          <div className="flex items-center gap-4">
            <Link to="/companions" className="hover:text-pink-600 transition-colors">
              Find Companion
            </Link>
            <Link to="/membership" className="hover:text-pink-600 transition-colors">
              Membership
            </Link>
            <Link to="/settings" className="hover:text-pink-600 transition-colors">
              Safety & Settings
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
